import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { _log } from '@shared/aux_helper_environment';

// Interfaces para las sugerencias de insights
export interface PriceSuggestion {
  item_id: number;
  store_id: number;
  new_price: number;
}

export interface InsightSuggestion {
  id: number;
  insight_id: number;
  type: 'price_change' | 'inventory_adjustment' | 'promotion_recommendation' | 'other';
  description: string;
  reason: string;
  suggestion_status: 'pending_approval' | 'approved' | 'rejected' | 'implemented';
  suggestions: PriceSuggestion[];
}

export interface InsightSuggestionsResponse {
  suggestions: InsightSuggestion[];
}

@Injectable({
  providedIn: 'root',
})
export class InsightsSuggestionsService {
  constructor() {
    _log('[InsightsSuggestionsService] Service initialized');
  }

  /**
   * Obtiene las sugerencias de insights pendientes
   * Por ahora devuelve data mockeada, eventualmente se conectará a la API real
   */
  getSuggestions(): Observable<InsightSuggestionsResponse> {
    _log('[InsightsSuggestionsService] Fetching insights suggestions...');

    const mockData: InsightSuggestionsResponse = {
      suggestions: [
        {
          id: 5,
          insight_id: 101,
          type: 'price_change',
          description: 'Cambios de precio sugeridos por el agente',
          reason: 'Subimos el precio ya que la baja de precio anterior no tuvo el efecto deseado en las ventas.',
          suggestion_status: 'pending_approval',
          suggestions: [
            {
              item_id: 12345,
              store_id: 222,
              new_price: 19.99,
            },
            {
              item_id: 12345,
              store_id: 223,
              new_price: 1.5,
            },
            {
              item_id: 12345,
              store_id: 224,
              new_price: 9.1,
            },
          ],
        },
        {
          id: 6,
          insight_id: 101,
          type: 'price_change',
          description: 'Cambios de precio sugeridos por el agente',
          reason: 'Bajamos los precios de productos sensibles ya que el ultimo aumento genero una caida fuerte de ventas.',
          suggestion_status: 'pending_approval',
          suggestions: [
            {
              item_id: 54321,
              store_id: 222,
              new_price: 22.39,
            },
            {
              item_id: 5432,
              store_id: 222,
              new_price: 12.5,
            },
          ],
        },
      ],
    };

    // Simular delay de red (500ms)
    return of(mockData).pipe(delay(500));
  }

  /**
   * Obtiene sugerencias filtradas por estado
   */
  getSuggestionsByStatus(status: InsightSuggestion['suggestion_status']): Observable<InsightSuggestionsResponse> {
    _log(`[InsightsSuggestionsService] Fetching suggestions with status: ${status}`);

    return this.getSuggestions().pipe(
      map(response => ({
        suggestions: response.suggestions.filter(suggestion => suggestion.suggestion_status === status),
      }))
    );
  }

  /**
   * Simula la aprobación de una sugerencia
   */
  approveSuggestion(suggestionId: number): Observable<{ success: boolean; message: string }> {
    _log(`[InsightsSuggestionsService] Approving suggestion ID: ${suggestionId}`);

    const response = {
      success: true,
      message: `Sugerencia ${suggestionId} aprobada exitosamente`,
    };

    return of(response).pipe(delay(300));
  }

  /**
   * Simula el rechazo de una sugerencia
   */
  rejectSuggestion(suggestionId: number, reason?: string): Observable<{ success: boolean; message: string }> {
    _log(`[InsightsSuggestionsService] Rejecting suggestion ID: ${suggestionId}, reason: ${reason}`);

    const response = {
      success: true,
      message: `Sugerencia ${suggestionId} rechazada${reason ? ': ' + reason : ''}`,
    };

    return of(response).pipe(delay(300));
  }

  /**
   * Verifica si hay nuevas sugerencias disponibles
   */
  checkForNewSuggestions(): Observable<{ hasNew: boolean; count: number }> {
    _log('[InsightsSuggestionsService] Checking for new suggestions...');

    // Simular que a veces hay nuevas sugerencias
    const hasNew = Math.random() > 0.5;
    const count = hasNew ? Math.floor(Math.random() * 5) + 1 : 0;

    return of({ hasNew, count }).pipe(delay(200));
  }
}
